import PropTypes from 'prop-types';
import React, {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { useSelector } from 'react-redux';

import Image from 'Component/Image';
// import LabelContainer from 'Component/LabelContainer';
import { BOTTOM_LC } from 'Component/LabelContainer/LabelContainer.config';
import Link from 'Component/Link';
import { ALT_IMAGE_NO_SELECTION } from 'Component/ProductCard/ProductCard.config';
import ProductLabels from 'Component/ProductLabels';
import { ProductType } from 'Type/ProductList.type';

import {
    INTERSECTION_THRESHOLD,
    LINK_TAB_INDEX,
    MIN_SWIPE_PX,
    resolveCardImages,
    SLIDE_THRESHOLD_PX,
    SLIDE_TRANSLATE_PCT,
    TEASE_END_DELAY_MS,
    TEASE_OFFSET_PCT,
    TEASE_START_DELAY_MS
} from './ProductCardImage.config';

/** @namespace Bodypwa/Component/ProductCardImage/Component/ProductCardImage */
export function ProductCardImage({
    url,
    name,
    selectedImagePath = undefined,
    altImage = undefined,
    baseSmallImage = undefined,
    wishlistButton,
    imageLinkClass = '',
    onClick = () => { },
    imageClass = '',
    isTeaseSwipe = false,
    isSwiper = false,
    product,
    categoryId = undefined,
    isVariantSelected = false
}) {
    const [showAlt, setShowAlt] = useState(false);
    const [current, setCurrent] = useState(0);
    const [tease, setTease] = useState(false);
    const hasTeasedRef = useRef(false);
    const containerRef = useRef(null);
    const { isMobile } = useSelector((state) => state.ConfigReducer.device) || {};
    const showModelImage = useSelector((state) => state.ConfigReducer.showModelImage) || false;

     const featuredProducts = useSelector(
        (state) => state.CategoryReducer?.category?.featured_products || ''
    );

    const touchStartX = useRef(null);
    const touchDeltaX = useRef(0);
    const hasSwiped = useRef(false);

    const { url: altImagePath, image_cover: altImageCover } = altImage || {};
    const { image_cover: baseImageCover } = baseSmallImage || {};
    const { mainSrc: resolvedMain, altSrc: resolvedAlt, isSwapped } = resolveCardImages(
        selectedImagePath, altImagePath, categoryId, isVariantSelected, showModelImage
    );
    const mainCover = isSwapped ? altImageCover : baseImageCover;
    const altCover = isSwapped ? baseImageCover : altImageCover;

    const isAltImageValid = Boolean(
        altImagePath && altImagePath !== ALT_IMAGE_NO_SELECTION
    );

    const checkAltImage = showAlt && isAltImageValid;

    useEffect(() => {
        if (!isMobile || !isTeaseSwipe || !isAltImageValid || hasTeasedRef.current) {
            return;
        }

        const node = containerRef.current;
        if (!node) {
            return;
        }

        let t1;
        let t2;
        const io = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting && entry.intersectionRatio >= INTERSECTION_THRESHOLD) {
                    hasTeasedRef.current = true;
                    t1 = setTimeout(() => setTease(true), TEASE_START_DELAY_MS);
                    t2 = setTimeout(() => setTease(false), TEASE_END_DELAY_MS);
                    io.disconnect();
                }
            },
            { rootMargin: '0px', threshold: INTERSECTION_THRESHOLD }
        );

        io.observe(node);
        return () => {
            io.disconnect();
            if (t1) {
                clearTimeout(t1);
            }
            if (t2) {
                clearTimeout(t2);
            }
        };
    }, [isMobile, isTeaseSwipe, isAltImageValid]);

    const imageSrc = useMemo(() => (
        checkAltImage
            ? resolvedAlt
            : resolvedMain
    ), [checkAltImage, resolvedAlt, resolvedMain]);

    const objectFitClass = useMemo(() => {
        const main = +mainCover ? 'object-cover' : 'object-contain';
        const alt = +altCover ? 'object-cover' : 'object-contain';
        return checkAltImage ? alt : main;
    }, [checkAltImage, mainCover, altCover]);

    const onMouseEnter = useCallback(() => {
        if (!isMobile && isAltImageValid) {
            setShowAlt(true);
        }
    }, [isMobile, isAltImageValid]);

    const onMouseLeave = useCallback(() => {
        if (!isMobile) {
            setShowAlt(false);
        }
    }, [isMobile]);

    const slides = useMemo(() => {
        const items = [
            {
                src: resolvedMain,
                fit: +mainCover ? 'object-cover' : 'object-contain',
                key: 'main'
            }
        ];

        if (isAltImageValid) {
            items.push({
                src: resolvedAlt,
                fit: +altCover ? 'object-cover' : 'object-contain',
                key: 'alt'
            });
        }

        return items;
    }, [resolvedMain, mainCover, isAltImageValid, resolvedAlt, altCover]);

    const basePct = -(current * SLIDE_TRANSLATE_PCT);
    const offsetPct = tease
        ? (current < (slides.length - 1) ? -TEASE_OFFSET_PCT : TEASE_OFFSET_PCT)
        : 0;
    const sliderTransform = `translateX(${basePct + offsetPct}%)`;

    const onTouchStart = useCallback((e) => {
        touchStartX.current = e.touches[0].clientX;
        touchDeltaX.current = 0;
        hasSwiped.current = false;
    }, []);

    const onTouchMove = useCallback((e) => {
        if (touchStartX.current === null) {
            return;
        }
        touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
        if (Math.abs(touchDeltaX.current) > MIN_SWIPE_PX) {
            hasSwiped.current = true;
        }
    }, []);

    const onTouchEnd = useCallback(() => {
        if (touchStartX.current === null) {
            return;
        }

        if (isMobile && !isSwiper) {
            if (touchDeltaX.current <= -SLIDE_THRESHOLD_PX && current < (slides.length - 1)) {
                setCurrent((i) => i + 1);
            } else if (touchDeltaX.current >= SLIDE_THRESHOLD_PX && current > 0) {
                setCurrent((i) => i - 1);
            }
        } else {
            if (isAltImageValid && touchDeltaX.current <= -SLIDE_THRESHOLD_PX) {
                setShowAlt(true);
            }
            if (isAltImageValid && touchDeltaX.current >= SLIDE_THRESHOLD_PX) {
                setShowAlt(false);
            }
        }

        touchStartX.current = null;
        touchDeltaX.current = 0;
    }, [isMobile, current, slides.length, isAltImageValid, isSwiper]);

    const handleClick = useCallback((e) => {
        if (hasSwiped.current) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (onClick) {
            onClick(e);
        }
    }, [onClick]);

    return (
        <Link
            to={ url }
            className={ `flex w-full relative ${imageLinkClass}` }
            tabIndex={ LINK_TAB_INDEX }
            onClick={ handleClick }
            onMouseEnter={ onMouseEnter }
            onMouseLeave={ onMouseLeave }
            onTouchStart={ onTouchStart }
            onTouchMove={ onTouchMove }
            onTouchEnd={ onTouchEnd }
        >
            { wishlistButton }
            { isMobile ? (
                <div className="overflow-hidden w-full h-full relative" ref={ containerRef }>
                    <div
                        className="flex transition-transform duration-500 ease-out w-full h-full"
                        style={ { transform: sliderTransform } }
                    >
                        { slides.map((s, idx) => (
                            <div key={ s.key } className="w-full h-full shrink-0">
                                <Image
                                    className={ imageClass }
                                    src={ s.src }
                                    alt={ name }
                                    imageCustomClass={ ` ${s.fit}` }
                                    loading={ idx === 0 ? 'eager' : 'lazy' }
                                />
                            </div>
                        )) }
                    </div>
                </div>
            ) : (
                <Image
                    className={ imageClass }
                    src={ imageSrc }
                    alt={ name }
                    imageCustomClass={ ` ${objectFitClass}` }
                    loading="lazy"
                />
            ) }
            <span className="bg-[rgba(7,7,7,0.03)] absolute z-10 top-0 left-0 w-full h-full pointer-events-none" />
              <ProductLabels
                  isFeaturedProduct={ featuredProducts?.includes(product.sku) }
                  product={ product }
                  position={ BOTTOM_LC }
              />
        </Link>
    );
}

/** @namespace Bodypwa/Component/ProductCardImage/Component/areEqualImage */
const areEqualImage = (prev, next) => (
    prev.url === next.url
    && prev.name === next.name
    && prev.selectedImagePath === next.selectedImagePath
    && prev.altImage?.path === next.altImage?.path
    && prev.altImage?.image_cover === next.altImage?.image_cover
    && prev.baseSmallImage?.image_cover === next.baseSmallImage?.image_cover
    && prev.className === next.className
    && prev.imageClass === next.imageClass
    && prev.isTeaseSwipe === next.isTeaseSwipe
    && prev.isSwiper === next.isSwiper
    && prev.categoryId === next.categoryId
    && prev.isVariantSelected === next.isVariantSelected
    && prev.wishlistButton === next.wishlistButton
    && prev.campaignLabel === next.campaignLabel
);

ProductCardImage.propTypes = {
    url: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
        state: PropTypes.shape({}),
        search: PropTypes.string
    }).isRequired,
    name: PropTypes.string.isRequired,
    selectedImagePath: PropTypes.string,
    altImage: PropTypes.shape({
        path: PropTypes.string,
        image_cover: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    baseSmallImage: PropTypes.shape({
        image_cover: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    wishlistButton: PropTypes.node.isRequired,
    imageLinkClass: PropTypes.string,
    onClick: PropTypes.func,
    imageClass: PropTypes.string,
    isTeaseSwipe: PropTypes.bool,
    categoryId: PropTypes.number,
    isVariantSelected: PropTypes.bool,
    isSwiper: PropTypes.bool,
    product: ProductType.isRequired
};

export default React.memo(ProductCardImage, areEqualImage);
